import { useEffect, useState } from "react";
import { useLang } from "@/i18n";
import api, { errMsg } from "@/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Check, X, DollarSign } from "lucide-react";

export default function BookingsTab({ onChanged }) {
  const { t } = useLang();
  const [bookings, setBookings] = useState([]);
  const [edits, setEdits] = useState({});

  const load = () => api.get("/admin/bookings").then((r) => setBookings(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  const patch = async (id, payload) => {
    try {
      await api.patch(`/admin/bookings/${id}`, payload);
      toast.success(t("admin.bookingUpdated"));
      load();
      onChanged?.();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const togglePaid = async (id) => {
    try {
      await api.post(`/admin/bookings/${id}/mark-paid`);
      load();
      onChanged?.();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="space-y-3" data-testid="bookings-tab">
      {bookings.length === 0 && <p className="text-muted-foreground">{t("admin.noData")}</p>}
      {bookings.map((b) => {
        const edit = edits[b.id] || {};
        return (
          <Card key={b.id} data-testid={`admin-booking-${b.id}`}>
            <CardContent className="p-4 space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="font-semibold">{b.name} <span className="font-normal text-muted-foreground">· {b.email}</span></p>
                  <p className="text-sm text-muted-foreground">{b.package_label} — ${b.price} · {t("admin.remainingLessons")}: {b.lessons_remaining}/{b.lessons_total}</p>
                  {b.preferred_times && <p className="text-xs text-muted-foreground mt-0.5">{t("booking.preferred")}: {b.preferred_times}</p>}
                  {b.goal && <p className="text-xs text-muted-foreground">{t("admin.goalLabel")}: {b.goal}</p>}
                </div>
                <div className="flex items-center gap-2">
                  <Badge className={b.status === "approved" ? "bg-teal-100 text-teal-900" : b.status === "declined" ? "bg-red-100 text-red-800" : "bg-amber-100 text-amber-900"} data-testid={`booking-status-${b.id}`}>
                    {b.status}
                  </Badge>
                  <Badge className={b.payment_status === "paid" ? "bg-secondary text-secondary-foreground" : "bg-purple-100 text-purple-800"} data-testid={`booking-pay-${b.id}`}>
                    {b.payment_status === "paid" ? t("admin.paidLabel") : t("admin.unpaidLabel")}
                  </Badge>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">{t("admin.schedule")}</Label>
                  <Input
                    type="datetime-local"
                    value={edit.scheduled_at ?? (b.scheduled_at || "")}
                    onChange={(e) => setEdits({ ...edits, [b.id]: { ...edit, scheduled_at: e.target.value } })}
                    data-testid={`booking-schedule-${b.id}`}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">{t("admin.teamsLink")}</Label>
                  <Input
                    placeholder="https://teams.microsoft.com/..."
                    value={edit.teams_link ?? (b.teams_link || "")}
                    onChange={(e) => setEdits({ ...edits, [b.id]: { ...edit, teams_link: e.target.value } })}
                    data-testid={`booking-teams-${b.id}`}
                  />
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {b.status === "pending" && (
                  <>
                    <Button size="sm" onClick={() => patch(b.id, { status: "approved", ...edit })} data-testid={`booking-approve-${b.id}`}>
                      <Check className="w-4 h-4 mr-1" /> {t("admin.approve")}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => patch(b.id, { status: "declined" })} data-testid={`booking-decline-${b.id}`}>
                      <X className="w-4 h-4 mr-1" /> {t("admin.decline")}
                    </Button>
                  </>
                )}
                {b.status !== "pending" && Object.keys(edit).length > 0 && (
                  <Button size="sm" variant="outline" onClick={() => { patch(b.id, edit); setEdits({ ...edits, [b.id]: {} }); }} data-testid={`booking-save-${b.id}`}>
                    {t("admin.save")}
                  </Button>
                )}
                <Button
                  size="sm"
                  variant={b.payment_status === "paid" ? "outline" : "secondary"}
                  onClick={() => togglePaid(b.id)}
                  data-testid={`booking-markpaid-${b.id}`}
                >
                  <DollarSign className="w-4 h-4 mr-1" /> {b.payment_status === "paid" ? t("admin.markUnpaid") : t("admin.markPaid")}
                </Button>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
